import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { Alert, Button, Card, DateBadge, Grid, Icon, Link, Text } from "~/components-shared";
import Picture from "~/components-shared/Picture";
import { AiAssistantRequestsEnum, AIEmbeddingContextEnum, AIQueryRoleEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import lists from "~/lib/lists";
import { useHistory } from "react-router-dom";
import Markdown from "~/components/Markdown";
import useAbacExt from "~/hooks/abac";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import aiQuerySelectors from "~/store/aiQueries/selectors";
import aiQueriesActionCreators from "~/store/aiQueries/actions";
import moment from "moment/moment";
import { genericColor } from "~/lib/resolvers";
const Wrapper = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  height: calc(80vh - 180px);
  padding: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 6;
}}px ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing.step * 6;
}}px 0
    ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.spacing.step * 6;
}}px;
`;
const Items = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding-right: 0.75em;
  margin-right: -0.75em;
  width: 100%;
`;
const MessageList = styled.div`
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column-reverse;
  padding-right: 0.75em;
  margin-right: -0.75em;
  width: 100%;
`;
const Message = styled.div`
  border-radius: 1rem;
  background-color: ${_ref4 => {
  let {
    theme,
    isPrimary
  } = _ref4;
  return isPrimary ? theme.colors.primary.main : theme.colors.grey.main;
}};
  color: ${_ref5 => {
  let {
    theme,
    isPrimary
  } = _ref5;
  return isPrimary ? theme.colors.background.primary : theme.colors.text.primary;
}};
  padding: 0.75em 1em;
`;
const AiAssistantContent = _ref6 => {
  let {
    showHistory,
    onHideHistory,
    contextData,
    onSuggestionSelect,
    onContextSelect
  } = _ref6;
  const theme = useTheme();
  const dispatch = useDispatch();
  const queries = useSelector(aiQuerySelectors.getAiQueries);
  const status = useSelector(aiQuerySelectors.getStatus);
  const {
    hasAfter
  } = useSelector(aiQuerySelectors.getPagination);
  const listError = useSelector(aiQuerySelectors.getError);
  const createError = useSelector(aiQuerySelectors.getErrorByQuery("+"));
  const error = createError || listError;
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const {
    userHasOneOf
  } = useAbacExt();
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(aiQueriesActionCreators.fetchStarted());
    }
  }, [status, dispatch]);
  const handleLoadMore = () => {
    dispatch(aiQueriesActionCreators.loadedMore());
  };
  const handleCardClick = (id, context) => {
    switch (context) {
      case AIEmbeddingContextEnum.UPCOMING_EVENTS:
        history.push(RoutesEnum.EVENT.replace(":eventId", id));
        break;
      case AIEmbeddingContextEnum.MEMBERS:
        history.push(RoutesEnum.MEMBER.replace(":memberId", id));
        break;
      case AIEmbeddingContextEnum.WORKING_STYLES:
        history.push(RoutesEnum.WORKING_STYLE.replace(":workingStyleId", id));
        break;
      case AIEmbeddingContextEnum.COMMUNITIES:
        history.push(RoutesEnum.COMMUNITY.replace(":communityId", id));
        break;
      case AIEmbeddingContextEnum.TOPICS:
      case AIEmbeddingContextEnum.TOPIC:
        history.push(RoutesEnum.TOPIC.replace(":topicId", id));
        break;
      default:
    }
  };
  if (!showHistory) {
    if (contextData.suggestions.length) {
      return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Button, {
        elemBefore: /*#__PURE__*/React.createElement(Icon, {
          src: "chevron",
          rotate: -90,
          s: {
            mr: 2
          }
        }),
        onClick: e => {
          e.stopPropagation();
          onContextSelect(AiAssistantRequestsEnum.GLOBAL_CONTEXT);
        },
        color: "transparent",
        rounded: true,
        s: {
          px: 3,
          py: 2,
          mb: 2
        }
      }, t("AiAssistantContent.goBack")), /*#__PURE__*/React.createElement(Items, null, /*#__PURE__*/React.createElement(Text, {
        color: "textSecondary",
        s: {
          mb: 3,
          ml: 3
        },
        display: "block"
      }, t("AiAssistantContent.examples"), ":"), contextData.suggestions.map(suggestion => /*#__PURE__*/React.createElement(Button, {
        key: suggestion,
        display: "block",
        color: "grey",
        s: {
          py: 2,
          px: 4,
          mb: 2,
          ml: 1
        },
        textAlign: "left",
        onClick: e => {
          e.stopPropagation();
          onSuggestionSelect(suggestion);
        }
      }, suggestion))));
    }
    return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Items, null, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      gap: 6,
      align: "stretch",
      equal: true,
      s: {
        m: 0
      }
    }, Object.keys(lists.ai.assistantRequests.names).filter(name => userHasOneOf(lists.ai.assistantRequests.authorities[name])).map(key => /*#__PURE__*/React.createElement(Card, {
      key: key,
      onClick: e => {
        e.stopPropagation();
        onContextSelect(key);
      },
      border: true
    }, /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      display: "block",
      weight: "semiBold",
      s: {
        py: 1.5
      }
    }, lists.ai.assistantRequests.names[key]), /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary"
    }, lists.ai.assistantRequests.descriptions[key]))))));
  }
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      rotate: -90,
      s: {
        mr: 2
      }
    }),
    onClick: e => {
      e.stopPropagation();
      onHideHistory();
    },
    color: "transparent",
    rounded: true,
    s: {
      px: 3,
      py: 2,
      mb: 2
    }
  }, t("AiAssistantContent.goBack")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), queries.length ? /*#__PURE__*/React.createElement(MessageList, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 3,
    fullWidth: true
  }, queries.sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf()).map(query => /*#__PURE__*/React.createElement(Grid.Cell, {
    key: query.id,
    justify: query.role === AIQueryRoleEnum.USER ? "end" : "start"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    s: {
      mb: 1,
      mx: 3
    },
    align: query.role === AIQueryRoleEnum.USER ? "right" : "left"
  }, query.role === AIQueryRoleEnum.ASSISTANT && "Assistant", /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "light",
    s: {
      mx: 2
    }
  }, moment(query.createdAt).local().startOf("seconds").fromNow()), query.role === AIQueryRoleEnum.USER && "You"), /*#__PURE__*/React.createElement(Message, {
    isPrimary: query.role === AIQueryRoleEnum.USER
  }, /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    display: "block",
    text: query.text,
    components: {
      a: _ref7 => {
        let {
          href = "",
          children
        } = _ref7;
        return /*#__PURE__*/React.createElement(Link, {
          href: href,
          color: theme.colors.text.primary,
          underline: true
        }, children.join(""));
      }
    }
  }), !!query.items && query.items.map(item => /*#__PURE__*/React.createElement(Card, {
    key: item.id,
    onClick: () => handleCardClick(item.id, query?.context),
    color: "transparent",
    s: {
      p: 2,
      mt: 2,
      mx: -1
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    align: "center",
    template: "3rem auto 18px"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, item.dateFrom ? /*#__PURE__*/React.createElement(DateBadge, {
    scale: 3.2,
    date: item.dateFrom
  }) : /*#__PURE__*/React.createElement(Picture, {
    width: "3rem",
    height: "3rem",
    name: item.name,
    src: item.image,
    border: "circled"
  })), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, item.role && /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    weight: "semiBold",
    display: "block",
    color: theme.colors.primary.main
  }, item.role), item.status && /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    weight: "semiBold",
    display: "block",
    color: genericColor(theme, lists.topics.statuses.colors[item.status])
  }, lists.topics.statuses.names[item.status]), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    lineHeight: 6,
    fontSize: 4,
    display: "block",
    weight: "semiBold"
  }, item.name), item.email && /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    weight: "semiBold",
    display: "block",
    color: "textSecondary"
  }, item.email), item.owner && /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    weight: "semiBold",
    display: "block",
    color: "textSecondary"
  }, "Owner: ", item.owner), item.memberCount && /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.text.secondary,
    weight: "regular",
    fontSize: 3.5
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    scale: 3
  }), " ", item.memberCount), item.attendeeCount && /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.text.secondary,
    weight: "regular",
    fontSize: 3.5
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    scale: 3
  }), " ", item.attendeeCount)), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    color: theme.colors.secondary.main,
    rotate: 90
  })))))))), hasAfter && /*#__PURE__*/React.createElement(Button, {
    onClick: handleLoadMore,
    loading: status === StatusStateEnum.LOADING,
    color: "transparent",
    s: {
      mb: 5
    }
  }, "Load more")) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block",
    style: {
      width: "100%"
    }
  }, t("AiAssistantContent.noQueries")));
};
export default AiAssistantContent;